import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiTrendingUp, FiShoppingCart, FiPieChart, FiBox, FiUsers, FiCreditCard,
  FiPrinter, FiRefreshCw, FiFileText, FiAlertTriangle, FiChevronDown, FiChevronRight,
} from 'react-icons/fi';
import {
  getReporteVentas, getReporteCompras, getReporteUtilidades, getReporteProductos,
  getReporteAlmacenVentas,
  getReporteCuentasCobrar, getReporteCuentasPagar, getReporteCobranzas,
  getClients, getProviders, getWarehouses, getEmpresaInfo,
} from '../services/api';
import { LineChart, BarChart, DoughnutChart } from '../components/Reportes/ReportesCharts';
import { imprimirReporte } from '../utils/imprimirReporte';
import '../styles/Reportes.css';

const fmtS = n => `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = n => parseFloat(n || 0).toLocaleString('es-PE', { maximumFractionDigits: 2 });
// Un producto puede venderse en más de un formato en el mismo rango (ej.
// HUEVO en JABA y en MEDIA JABA) — sigue siendo UN producto = UNA fila, la
// cantidad se muestra como la suma de cada formato en SU propia unidad,
// nunca convertida a la unidad base.
const fmtFormatos = formatos => (formatos || []).map(f => `${fmtN(f.qty)} ${f.unit_measure || ''}`.trim()).join(' + ');
const fmtDate = v => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? String(v).slice(0, 10) : d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Lima' });
};
// due_date es DATE pura (sin hora, medianoche UTC) — timeZone la correría un
// día atrás; se extrae el día calendario directo del string ISO, sin Date.
const fmtDateOnly = v => { if (!v) return '—'; const m = String(v).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v).slice(0, 10); };

// Estructura del sidebar: items planos + grupos "Almacén" (Productos +
// Ventas por producto) y "Cobranzas" (Cuentas por Cobrar + Reporte de
// Cobranzas). `FLAT_TABS` (más abajo) aplana todo para las búsquedas por
// key/label.
const TABS = [
  { key: 'ventas',          label: 'Ventas',              icon: <FiTrendingUp /> },
  { key: 'compras',         label: 'Compras',              icon: <FiShoppingCart /> },
  { key: 'utilidades',      label: 'Utilidades',           icon: <FiPieChart /> },
  { group: 'Almacén', icon: <FiBox />, children: [
    { key: 'productos',           label: 'Productos' },
    { key: 'almacen-ventas',      label: 'Ventas' },
    { key: 'ventas-x-producto',   label: 'Ventas x Prod.' },
  ] },
  { group: 'Cobranzas', icon: <FiUsers />, children: [
    { key: 'cuentas-cobrar', label: 'Cuentas por Cobrar' },
    { key: 'cobranzas',      label: 'Reporte de Cobranzas' },
  ] },
  { key: 'cuentas-pagar',   label: 'Ctas. por Pagar',       icon: <FiCreditCard /> },
];

const FLAT_TABS = TABS.flatMap(t => t.children ? t.children : [t]);

const FETCHERS = {
  ventas: getReporteVentas, compras: getReporteCompras, utilidades: getReporteUtilidades,
  productos: getReporteProductos, 'almacen-ventas': getReporteAlmacenVentas,
  // "Ventas x Prod." reutiliza el mismo endpoint/datos de Almacén > Ventas
  // (ya trae el desglose por venta en `porProducto[].ventas`) — solo cambia
  // la presentación: tabla plana sin expandir, pensada para imprimir.
  'ventas-x-producto': getReporteAlmacenVentas,
  'cuentas-cobrar': getReporteCuentasCobrar, 'cuentas-pagar': getReporteCuentasPagar,
  cobranzas: getReporteCobranzas,
};

// Pestañas con default de rango propio (distinto del "mes actual" que
// comparten Compras/Utilidades/Productos/Ctas. Cobrar/Ctas. Pagar) — ver
// `defaultRangoParaTab` y su uso más abajo.
const TABS_CON_DEFAULT_PROPIO = ['ventas', 'almacen-ventas', 'ventas-x-producto', 'cobranzas'];

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtYMD(y, m, d) { return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`; }
function rangoRapido(tipo) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  if (tipo === 'hoy') return { desde: fmtYMD(y, m, d), hasta: fmtYMD(y, m, d) };
  if (tipo === 'semana') {
    const dow = now.getDay();
    const diff = (dow + 6) % 7;
    const monday = new Date(y, m, d - diff);
    const sunday = new Date(y, m, d - diff + 6);
    return { desde: fmtYMD(monday.getFullYear(), monday.getMonth(), monday.getDate()), hasta: fmtYMD(sunday.getFullYear(), sunday.getMonth(), sunday.getDate()) };
  }
  if (tipo === 'anio') return { desde: fmtYMD(y, 0, 1), hasta: fmtYMD(y, m, d) };
  return { desde: fmtYMD(y, m, 1), hasta: fmtYMD(y, m, d) }; // mes (default)
}

// Rango por defecto según la pestaña — usado tanto en el estado inicial
// (primer render, entra directo a esta pestaña por URL) como al cambiar de
// pestaña en medio de la sesión, para que ambos casos den el mismo resultado.
// - Ventas y Almacén > Ventas: reportes operativos del día a día -> HOY.
// - Cobranzas: estado de cuenta por cliente, "mes actual" esconde historial
//   previo -> rango amplio.
// - El resto (Compras/Utilidades/Productos/Ctas. Cobrar/Ctas. Pagar): mes actual.
function defaultRangoParaTab(tabKey) {
  if (tabKey === 'cobranzas') return { desde: '2020-01-01', hasta: todayLocal() };
  if (tabKey === 'ventas' || tabKey === 'almacen-ventas' || tabKey === 'ventas-x-producto') return rangoRapido('hoy');
  return rangoRapido('mes');
}

// ── Tabla genérica ──────────────────────────────────────────────────────────
function Tabla({ titulo, icono, cols, rows, vacio = 'Sin datos' }) {
  return (
    <div className="rp-card">
      <div className="rp-card-title">{icono}{titulo}</div>
      <div className="rp-table-wrap">
        <table className="rp-table">
          <thead><tr>{cols.map(c => <th key={c.k} style={{ textAlign: c.align || 'left' }}>{c.label}</th>)}</tr></thead>
          <tbody>
            {(!rows || rows.length === 0) ? (
              <tr><td colSpan={cols.length} className="rp-empty">{vacio}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i}>{cols.map(c => (
                <td key={c.k} style={{ textAlign: c.align || 'left' }}>{c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? '—')}</td>
              ))}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone = 'default' }) {
  return (
    <div className={`rp-kpi rp-kpi-${tone}`}>
      <div className="rp-kpi-label">{label}</div>
      <div className="rp-kpi-value">{value}</div>
    </div>
  );
}

const ESTADO_LABELS = { cancelled: 'ANULADA', completed: 'COMPLETADO', pending: 'PENDIENTE', partial: 'PARCIAL', paid: 'PAGADO' };
const badgeEstadoLabel = v => ESTADO_LABELS[String(v).toLowerCase()] || String(v || '').toUpperCase();
const badgeEstado = v => {
  const up = String(v || '').toUpperCase();
  const cls = up === 'CANCELLED' || up === 'ANULADA' ? 'rp-badge-bad'
    : (up === 'PAID' || up === 'COBRADO' || up === 'PAGADO') ? 'rp-badge-ok'
    : 'rp-badge-warn';
  return <span className={`rp-badge ${cls}`}>{badgeEstadoLabel(v)}</span>;
};

export default function ReportesPage({ businessId }) {
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const tab = FLAT_TABS.some(t => t.key === tabParam) ? tabParam : 'ventas';
  const [groupOpen, setGroupOpen] = useState({ Cobranzas: true, Almacén: true });

  const initialRango = defaultRangoParaTab(tab);
  const [desde, setDesde] = useState(initialRango.desde);
  const [hasta, setHasta] = useState(initialRango.hasta);
  const [warehouseId, setWarehouseId] = useState('');
  const [diasSinMov, setDiasSinMov] = useState(30);
  const [clientId, setClientId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [estadoCobranza, setEstadoCobranza] = useState('todo'); // 'todo' | 'con_deuda'
  const [expandedProd, setExpandedProd] = useState(new Set()); // Almacén > Ventas: product_id con desglose abierto

  const [clients, setClients] = useState([]);
  const [providers, setProviders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [empresa, setEmpresa] = useState(null);

  const [data, setData] = useState(null);
  const [dataTab, setDataTab] = useState(null); // a qué tab pertenece `data` — evita pintar con la forma equivocada
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Siempre refleja el `tab` del render más reciente — permite que `cargar()`
  // descarte una respuesta de red que llega tarde para una pestaña que el
  // usuario ya abandonó (evita pintar datos con la forma equivocada).
  const tabRef = useRef(tab);
  tabRef.current = tab;

  useEffect(() => {
    if (!businessId) return;
    getClients(businessId).then(r => setClients(r.data?.data || [])).catch(() => {});
    getProviders(businessId).then(r => setProviders(r.data?.data || [])).catch(() => {});
    getWarehouses(businessId).then(r => setWarehouses(r.data?.data || [])).catch(() => {});
    getEmpresaInfo(businessId).then(r => setEmpresa(r.data?.data || null)).catch(() => {});
  }, [businessId]);

  const cargar = useCallback(async () => {
    if (!businessId) return;
    // El reporte de Cobranzas es por cliente obligatorio — sin cliente
    // seleccionado no hay nada que pedir al backend (que además lo rechaza).
    if (tab === 'cobranzas' && !clientId) { setData(null); setError(''); return; }
    const requestTab = tab;
    setLoading(true); setError('');
    try {
      const params = { desde, hasta };
      if (tab === 'productos') { params.warehouse_id = warehouseId; params.dias_sin_movimiento = diasSinMov; }
      if (tab === 'compras')          params.provider_id = providerId;
      if (tab === 'cuentas-cobrar')   params.client_id = clientId;
      if (tab === 'cuentas-pagar')    params.provider_id = providerId;
      if (tab === 'cobranzas')        { params.client_id = clientId; params.estado = estadoCobranza; }
      const res = await FETCHERS[tab](businessId, params);
      if (requestTab !== tabRef.current) return; // respuesta obsoleta (el usuario ya cambió de pestaña)
      setData(res.data?.data || null);
      setDataTab(requestTab);
    } catch (e) {
      if (requestTab !== tabRef.current) return;
      setError(e.response?.data?.error || e.message);
      setData(null);
      setDataTab(requestTab);
    } finally { setLoading(false); }
  }, [businessId, tab, desde, hasta, warehouseId, diasSinMov, clientId, providerId, estadoCobranza]);

  // Al cambiar de pestaña, cada reporte tiene una forma distinta de charts/tables —
  // limpiar data de inmediato evita que se intente leer una clave que no existe en
  // la forma del reporte anterior mientras se espera la respuesta del nuevo.
  useEffect(() => { setData(null); setError(''); setExpandedProd(new Set()); }, [tab]);

  // Un mismo producto puede aparecer en dos filas de "Almacén > Ventas" si se
  // vendió tanto suelto como en una presentación (ej. HUEVO en UN y en JABA)
  // — la clave de expand/detalle debe distinguirlas, product_id solo no
  // alcanza (ambas filas comparten el mismo product_id).
  const rowKey = (r) => `${r.product_id}::${r.presentation_name || ''}`;

  const toggleProd = (key) => {
    setExpandedProd(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Pestañas con default de rango propio (distinto del "mes actual" que
  // comparten Compras/Utilidades/Productos/Ctas. Cobrar/Ctas. Pagar): al
  // entrar a una de estas — ya sea de entrada (mount directo por URL, ver
  // `initialRango` arriba) o cambiando de pestaña en medio de la sesión —
  // se fuerza su propio default vía `defaultRangoParaTab`. El resto de
  // pestañas SÍ comparten el rango entre sí (cambiar de Compras a
  // Utilidades conserva el rango elegido), a propósito.
  const prevTabRef = useRef(tab);
  useEffect(() => {
    if (TABS_CON_DEFAULT_PROPIO.includes(tab) && prevTabRef.current !== tab) {
      const r = defaultRangoParaTab(tab);
      setDesde(r.desde);
      setHasta(r.hasta);
    }
    prevTabRef.current = tab;
  }, [tab]);

  useEffect(() => { cargar(); }, [cargar]);

  const setTab = (key) => navigate(`/reportes/${key}`);

  const aplicarRangoRapido = (tipo) => {
    const r = rangoRapido(tipo);
    setDesde(r.desde); setHasta(r.hasta);
  };

  // ── KPIs por pestaña ────────────────────────────────────────────────────
  function kpisPestaña() {
    if (!data || dataTab !== tab) return [];
    const k = data.kpis || {};
    switch (tab) {
      case 'ventas': return [
        { label: 'N° Ventas', value: k.n_ventas },
        { label: 'Total Vendido', value: fmtS(k.total_vendido), tone: 'ok' },
        { label: 'Contado', value: fmtS(k.contado) },
        { label: 'Crédito', value: fmtS(k.credito), tone: 'warn' },
        { label: 'Descuentos', value: fmtS(k.descuentos) },
        { label: 'Anuladas', value: `${fmtS(k.anuladas_total)} (${k.anuladas_n})`, tone: 'bad' },
      ];
      case 'compras': return [
        { label: 'N° Compras', value: k.n_compras },
        { label: 'Total Comprado', value: fmtS(k.total_comprado), tone: 'ok' },
        { label: 'Compra Promedio', value: fmtS(k.promedio) },
        { label: 'Contado', value: fmtS(k.contado) },
        { label: 'Crédito', value: fmtS(k.credito), tone: 'warn' },
        { label: 'Anuladas', value: `${fmtS(k.anuladas_total)} (${k.anuladas_n})`, tone: 'bad' },
      ];
      case 'utilidades': return [
        { label: 'Ingresos', value: fmtS(k.ingresos), tone: 'ok' },
        { label: 'Costo', value: fmtS(k.costo), tone: 'warn' },
        { label: 'Utilidad Bruta', value: fmtS(k.utilidad_bruta), tone: k.utilidad_bruta >= 0 ? 'ok' : 'bad' },
        { label: 'Margen %', value: `${(k.margen || 0).toFixed(1)}%` },
        { label: 'Descuentos', value: fmtS(k.descuentos) },
      ];
      case 'productos': return [
        { label: 'Productos con Stock', value: k.productos_con_stock },
        { label: 'Unidades', value: fmtN(k.unidades) },
        { label: 'Valor Costo', value: fmtS(k.valor_costo), tone: 'warn' },
        { label: 'Valor Venta', value: fmtS(k.valor_venta), tone: 'ok' },
        { label: 'Stock Bajo', value: k.stock_bajo, tone: 'bad' },
        { label: 'Sin Movimiento', value: k.sin_movimiento, tone: 'bad' },
      ];
      case 'almacen-ventas':
      case 'ventas-x-producto':
        return [
          { label: 'N° Productos Vendidos', value: k.n_productos },
          { label: 'Total Vendido', value: fmtS(k.total_vendido), tone: 'ok' },
        ];
      case 'cuentas-cobrar': return [
        { label: 'N° Cuentas', value: k.n_cuentas },
        { label: 'Total Crédito', value: fmtS(k.total_credito) },
        { label: 'Cobrado', value: fmtS(k.cobrado), tone: 'ok' },
        { label: 'Saldo Pendiente', value: fmtS(k.saldo_pendiente), tone: 'warn' },
        { label: 'Vencido', value: `${fmtS(k.vencido_total)} (${k.vencido_n})`, tone: 'bad' },
        { label: 'Cobros del Periodo', value: `${fmtS(k.cobros_periodo_total)} (${k.cobros_periodo_n})`, tone: 'ok' },
      ];
      case 'cuentas-pagar': return [
        { label: 'N° Cuentas', value: k.n_cuentas },
        { label: 'Total Crédito', value: fmtS(k.total_credito) },
        { label: 'Pagado', value: fmtS(k.pagado), tone: 'ok' },
        { label: 'Saldo Pendiente', value: fmtS(k.saldo_pendiente), tone: 'warn' },
        { label: 'Vencido', value: `${fmtS(k.vencido_total)} (${k.vencido_n})`, tone: 'bad' },
        { label: 'Pagos del Periodo', value: `${fmtS(k.pagos_periodo_total)} (${k.pagos_periodo_n})`, tone: 'ok' },
      ];
      case 'cobranzas': return [
        { label: 'N° Ventas a Crédito', value: k.n_ventas_credito },
        { label: 'Total Crédito', value: fmtS(k.total_credito) },
        { label: 'Total Cobrado', value: fmtS(k.total_cobrado), tone: 'ok' },
        { label: 'Saldo Pendiente', value: fmtS(k.saldo_pendiente), tone: 'warn' },
        { label: 'Con Deuda / Pagadas', value: `${k.n_con_deuda} / ${k.n_pagadas}` },
        { label: 'Inconsistencias', value: k.n_inconsistencias, tone: k.n_inconsistencias > 0 ? 'bad' : 'ok' },
      ];
      default: return [];
    }
  }

  const handleImprimir = () => {
    if (!data || dataTab !== tab) return;
    const wh = warehouses.find(w => String(w.id) === String(warehouseId));
    let filtrosDesc = `Periodo: ${fmtDateOnly(desde)} — ${fmtDateOnly(hasta)}`;
    if (tab === 'productos' && wh) filtrosDesc += `  |  Almacén: ${wh.name}`;
    imprimirReporte(tab, data, { empresa, kpis: kpisPestaña(), filtrosDesc });
  };

  return (
    <div className="rp-container">
      <div className="rp-sidebar">
        <div className="rp-sidebar-title">REPORTES</div>
        {TABS.map((t, i) => t.children ? (
          <React.Fragment key={t.group}>
            <button
              className={`rp-sidebar-item rp-sidebar-group ${t.children.some(c => c.key === tab) ? 'active' : ''}`}
              onClick={() => setGroupOpen(o => ({ ...o, [t.group]: !o[t.group] }))}
            >
              {t.icon} <span>{t.group}</span>
              {groupOpen[t.group] ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
            </button>
            {groupOpen[t.group] && t.children.map(c => (
              <button key={c.key} className={`rp-sidebar-item rp-sidebar-subitem ${tab === c.key ? 'active' : ''}`} onClick={() => setTab(c.key)}>
                <span className="rp-sidebar-submark">–</span>
                <span>{c.label}</span>
              </button>
            ))}
          </React.Fragment>
        ) : (
          <button key={t.key} className={`rp-sidebar-item ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.icon} <span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="rp-main">
        <div className="rp-toolbar">
          <div className="rp-toolbar-title">{FLAT_TABS.find(t => t.key === tab)?.label}</div>
          <div className="rp-toolbar-actions">
            <div className="rp-chips">
              <button className="rp-chip" onClick={() => aplicarRangoRapido('hoy')}>Hoy</button>
              <button className="rp-chip" onClick={() => aplicarRangoRapido('semana')}>Semana</button>
              <button className="rp-chip" onClick={() => aplicarRangoRapido('mes')}>Mes</button>
              <button className="rp-chip" onClick={() => aplicarRangoRapido('anio')}>Año</button>
            </div>
            <input type="date" className="rp-date" value={desde} onChange={e => setDesde(e.target.value)} />
            <span className="rp-sep">—</span>
            <input type="date" className="rp-date" value={hasta} onChange={e => setHasta(e.target.value)} />

            {tab === 'productos' && (
              <>
                <select className="rp-select" value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
                  <option value="">Todos los almacenes</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <input type="number" className="rp-input-num" min="1" value={diasSinMov}
                  onChange={e => setDiasSinMov(e.target.value)} title="Días sin movimiento" />
              </>
            )}
            {tab === 'compras' && (
              <select className="rp-select" value={providerId} onChange={e => setProviderId(e.target.value)}>
                <option value="">Todos los proveedores</option>
                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            {tab === 'cuentas-cobrar' && (
              <select className="rp-select" value={clientId} onChange={e => setClientId(e.target.value)}>
                <option value="">Todos los clientes</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.nombre_completo || c.nombres}</option>)}
              </select>
            )}
            {tab === 'cuentas-pagar' && (
              <select className="rp-select" value={providerId} onChange={e => setProviderId(e.target.value)}>
                <option value="">Todos los proveedores</option>
                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            {tab === 'cobranzas' && (
              <>
                <select className="rp-select" value={clientId} onChange={e => setClientId(e.target.value)}>
                  <option value="">Selecciona un cliente...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.nombre_completo || c.nombres}</option>)}
                </select>
                <select className="rp-select" value={estadoCobranza} onChange={e => setEstadoCobranza(e.target.value)}>
                  <option value="todo">Todo histórico</option>
                  <option value="con_deuda">Solo con deuda</option>
                </select>
              </>
            )}

            <button className="rp-btn-secondary" onClick={cargar} disabled={loading}>
              <FiRefreshCw size={13} className={loading ? 'rp-spin' : ''} /> Buscar
            </button>
            <button className="rp-btn-primary" onClick={handleImprimir} disabled={!data || dataTab !== tab}>
              <FiPrinter size={13} /> Imprimir
            </button>
          </div>
        </div>

        {error && <div className="rp-error">{error}</div>}

        {(() => { const dataReady = data && dataTab === tab; return loading && !dataReady ? (
          <div className="rp-loading">Cargando...</div>
        ) : tab === 'cobranzas' && !clientId ? (
          <div className="rp-empty" style={{ padding: 40, textAlign: 'center' }}>
            <FiUsers size={22} style={{ marginBottom: 8 }} /><br />
            Selecciona un cliente para ver su reporte de cobranzas.
          </div>
        ) : !dataReady ? null : (
          <>
            <div className="rp-kpis">
              {kpisPestaña().map((k, i) => <KpiCard key={i} {...k} />)}
            </div>

            {tab === 'ventas' && (
              <div className="rp-two-col">
                <div className="rp-card">
                  <div className="rp-card-title"><FiTrendingUp /> Ventas por Día</div>
                  <LineChart labels={data.charts.porDia.map(r => fmtDateOnly(r.d))} data={data.charts.porDia.map(r => parseFloat(r.total))} color="#0ea5e9" />
                </div>
                <div className="rp-card">
                  <div className="rp-card-title"><FiPieChart /> Formas de Pago</div>
                  <DoughnutChart items={data.charts.formaPago.map(f => ({ label: f.method, value: f.total }))} />
                </div>
              </div>
            )}
            {tab === 'compras' && (
              <div className="rp-two-col">
                <div className="rp-card">
                  <div className="rp-card-title"><FiShoppingCart /> Compras por Día</div>
                  <BarChart labels={data.charts.porDia.map(r => fmtDateOnly(r.d))} series={[{ label: 'Compras', data: data.charts.porDia.map(r => parseFloat(r.total)), color: '#16a34a' }]} />
                </div>
                <div className="rp-card">
                  <div className="rp-card-title"><FiPieChart /> Por Proveedor</div>
                  <DoughnutChart items={data.charts.porProveedor.map(f => ({ label: f.provider_name, value: f.total }))} />
                </div>
              </div>
            )}
            {tab === 'utilidades' && (
              <div className="rp-card">
                <div className="rp-card-title"><FiPieChart /> Ingresos vs Costo vs Utilidad por Día</div>
                <BarChart
                  labels={data.charts.porDia.map(r => fmtDateOnly(r.d))}
                  series={[
                    { label: 'Ingresos', data: data.charts.porDia.map(r => r.ingresos), color: '#0ea5e9' },
                    { label: 'Costo', data: data.charts.porDia.map(r => r.costo), color: '#d97706' },
                    { label: 'Utilidad', data: data.charts.porDia.map(r => r.utilidad), color: '#16a34a' },
                  ]}
                />
              </div>
            )}
            {tab === 'cuentas-cobrar' && (
              <div className="rp-two-col">
                <div className="rp-card">
                  <div className="rp-card-title"><FiUsers /> Saldo por Cliente</div>
                  <BarChart labels={data.charts.saldoPorCliente.map(r => r.client_name)} series={[{ label: 'Saldo', data: data.charts.saldoPorCliente.map(r => r.saldo), color: '#d97706' }]} />
                </div>
                <div className="rp-card">
                  <div className="rp-card-title"><FiPieChart /> Antigüedad de Deuda</div>
                  <DoughnutChart
                    items={data.charts.antiguedad.map(a => ({ label: a.bucket, value: a.total }))}
                    colors={['#16a34a', '#d97706', '#f97316', '#dc2626']}
                  />
                </div>
              </div>
            )}
            {tab === 'cuentas-pagar' && (
              <div className="rp-card">
                <div className="rp-card-title"><FiCreditCard /> Saldo por Proveedor</div>
                <BarChart labels={data.charts.saldoPorProveedor.map(r => r.provider_name)} series={[{ label: 'Saldo', data: data.charts.saldoPorProveedor.map(r => r.saldo), color: '#7c3aed' }]} />
              </div>
            )}

            {tab === 'ventas' && (
              <>
                <Tabla titulo="Ventas por Producto" icono={<FiBox />} rows={data.tables.porProducto} cols={[
                  { k: 'product_name', label: 'Producto' },
                  { k: 'qty', label: 'Cantidad', align: 'right', fmt: (v, r) => `${fmtN(v)} ${r.unit_measure || ''}` },
                  { k: 'qty_unidad', label: 'Unidades', align: 'right', fmt: (v, r) => r.special_condition ? `${fmtN(v)} UND` : '—' },
                  { k: 'total', label: 'Total S/', align: 'right', fmt: fmtS },
                ]} />
                <Tabla titulo="Ventas por Vendedor" icono={<FiUsers />} rows={data.tables.porVendedor} cols={[
                  { k: 'seller_name', label: 'Vendedor' },
                  { k: 'n_ventas', label: 'N° Ventas', align: 'right' },
                  { k: 'total', label: 'Total', align: 'right', fmt: fmtS },
                ]} />
                <Tabla titulo="Detalle de Ventas" icono={<FiTrendingUp />} rows={data.tables.detalle} cols={[
                  { k: 'sale_date', label: 'Fecha', fmt: fmtDate },
                  { k: 'sale_number', label: 'N° Venta' },
                  { k: 'document_type', label: 'Documento' },
                  { k: 'client_name', label: 'Cliente' },
                  { k: 'total', label: 'Total', align: 'right', fmt: fmtS },
                  { k: 'status', label: 'Estado', fmt: badgeEstado },
                ]} />
              </>
            )}

            {tab === 'compras' && (
              <>
                <div className="rp-two-col">
                  <Tabla titulo="Compras por Proveedor" icono={<FiShoppingCart />} rows={data.tables.porProveedor} cols={[
                    { k: 'provider_name', label: 'Proveedor' },
                    { k: 'n', label: 'N° Compras', align: 'right' },
                    { k: 'total', label: 'Total', align: 'right', fmt: fmtS },
                  ]} />
                  <Tabla titulo="Top Productos Comprados" icono={<FiBox />} rows={data.tables.topProductos} cols={[
                    { k: 'product_name', label: 'Producto' },
                    { k: 'qty', label: 'Cant.', align: 'right', fmt: fmtN },
                    { k: 'total', label: 'Total', align: 'right', fmt: fmtS },
                  ]} />
                </div>
                <Tabla titulo="Detalle de Compras" icono={<FiShoppingCart />} rows={data.tables.detalle} cols={[
                  { k: 'purchase_date', label: 'Fecha', fmt: fmtDate },
                  { k: 'purchase_number', label: 'N° Compra' },
                  { k: 'provider_name', label: 'Proveedor' },
                  { k: 'total', label: 'Total', align: 'right', fmt: fmtS },
                  { k: 'status', label: 'Estado', fmt: badgeEstado },
                ]} />
              </>
            )}

            {tab === 'utilidades' && (
              <div className="rp-two-col">
                <Tabla titulo="Utilidad por Producto" icono={<FiBox />} rows={data.tables.porProducto} cols={[
                  { k: 'product_name', label: 'Producto' },
                  { k: 'ingresos', label: 'Ingresos', align: 'right', fmt: fmtS },
                  { k: 'costo', label: 'Costo', align: 'right', fmt: fmtS },
                  { k: 'utilidad', label: 'Utilidad', align: 'right', fmt: v => <span style={{ color: v >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>{fmtS(v)}</span> },
                  { k: 'margen', label: 'Margen %', align: 'right', fmt: v => `${v.toFixed(1)}%` },
                ]} />
                <Tabla titulo="Utilidad por Familia" icono={<FiPieChart />} rows={data.tables.porFamilia} cols={[
                  { k: 'family', label: 'Familia' },
                  { k: 'ingresos', label: 'Ingresos', align: 'right', fmt: fmtS },
                  { k: 'costo', label: 'Costo', align: 'right', fmt: fmtS },
                  { k: 'utilidad', label: 'Utilidad', align: 'right', fmt: v => <span style={{ color: v >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>{fmtS(v)}</span> },
                  { k: 'margen', label: 'Margen %', align: 'right', fmt: v => `${v.toFixed(1)}%` },
                ]} />
              </div>
            )}

            {tab === 'productos' && (
              <>
                <div className="rp-two-col">
                  <Tabla titulo="Valorización por Almacén" icono={<FiBox />} rows={data.tables.valorizacionPorAlmacen} cols={[
                    { k: 'warehouse_name', label: 'Almacén' },
                    { k: 'n_productos', label: 'Productos', align: 'right' },
                    { k: 'valor_costo', label: 'Valor Costo', align: 'right', fmt: fmtS },
                    { k: 'valor_venta', label: 'Valor Venta', align: 'right', fmt: fmtS },
                  ]} />
                  <Tabla titulo="Top Vendidos en el Rango" icono={<FiTrendingUp />} rows={data.tables.topVendidos} cols={[
                    { k: 'product_name', label: 'Producto' },
                    { k: 'qty', label: 'Cant.', align: 'right', fmt: fmtN },
                    { k: 'total', label: 'Total', align: 'right', fmt: fmtS },
                  ]} />
                </div>
                <div className="rp-two-col">
                  <Tabla titulo="Stock Bajo Mínimo" icono={<FiBox />} rows={data.tables.stockBajo} cols={[
                    { k: 'sku', label: 'SKU' },
                    { k: 'name', label: 'Producto' },
                    { k: 'warehouse_name', label: 'Almacén' },
                    { k: 'stock', label: 'Stock', align: 'right', fmt: fmtN },
                    { k: 'deficit', label: 'Faltante', align: 'right', fmt: v => <span style={{ color: '#dc2626', fontWeight: 700 }}>{fmtN(v)}</span> },
                  ]} />
                  <Tabla titulo={`Sin Movimiento (${diasSinMov}+ días)`} icono={<FiBox />} rows={data.tables.sinMovimiento} cols={[
                    { k: 'sku', label: 'SKU' },
                    { k: 'name', label: 'Producto' },
                    { k: 'ultima', label: 'Última Venta/Mov.', fmt: v => v ? fmtDate(v) : 'Nunca' },
                    { k: 'stock', label: 'Stock', align: 'right', fmt: fmtN },
                  ]} />
                </div>
                <Tabla titulo="Inventario Valorizado" icono={<FiBox />} rows={data.tables.inventarioCompleto} cols={[
                  { k: 'sku', label: 'SKU' },
                  { k: 'name', label: 'Producto' },
                  { k: 'family', label: 'Familia' },
                  { k: 'stock', label: 'Stock', align: 'right', fmt: fmtN },
                  { k: 'cost_price', label: 'P. Costo', align: 'right', fmt: fmtS },
                  { k: 'unit_price', label: 'P. Venta', align: 'right', fmt: fmtS },
                  { k: 'valor_costo', label: 'Valorizado', align: 'right', fmt: fmtS },
                ]} />
              </>
            )}

            {tab === 'almacen-ventas' && (
              <div className="rp-card">
                <div className="rp-card-title">
                  <FiBox /> Ventas por Producto — {desde === hasta ? fmtDateOnly(desde) : `${fmtDateOnly(desde)} — ${fmtDateOnly(hasta)}`}
                </div>
                <div className="rp-table-wrap">
                  <table className="rp-table">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th style={{ textAlign: 'right' }}>Cantidad</th>
                        <th style={{ textAlign: 'right' }}>Unidades</th>
                        <th style={{ textAlign: 'right' }}>Total S/</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!data.tables.porProducto || data.tables.porProducto.length === 0) ? (
                        <tr><td colSpan={4} className="rp-empty">Sin ventas en el rango seleccionado</td></tr>
                      ) : data.tables.porProducto.map((r, i) => {
                        const hasVentas = r.ventas && r.ventas.length > 0;
                        const isOpen = hasVentas && expandedProd.has(rowKey(r));
                        return (
                          <React.Fragment key={rowKey(r) || i}>
                            <tr
                              onClick={() => hasVentas && toggleProd(rowKey(r))}
                              style={{ cursor: hasVentas ? 'pointer' : 'default' }}
                            >
                              <td>
                                {hasVentas && (isOpen
                                  ? <FiChevronDown style={{ verticalAlign: 'middle', marginRight: 4, color: '#64748b' }} />
                                  : <FiChevronRight style={{ verticalAlign: 'middle', marginRight: 4, color: '#64748b' }} />)}
                                {r.product_name}
                              </td>
                              <td style={{ textAlign: 'right' }}>{fmtFormatos(r.formatos)}</td>
                              <td style={{ textAlign: 'right' }}>{r.special_condition ? `${fmtN(r.qty_unidad)} UND` : '—'}</td>
                              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtS(r.total)}</td>
                            </tr>
                            {isOpen && (
                              <tr>
                                <td colSpan={4} style={{ padding: '4px 8px 10px', background: '#f8fafc' }}>
                                  <table className="rp-table rp-subtable">
                                    <thead>
                                      <tr>
                                        <th>Fecha</th><th>Documento</th><th>Cliente</th>
                                        <th style={{ textAlign: 'right' }}>Cantidad</th>
                                        <th style={{ textAlign: 'right' }}>Unidades</th>
                                        <th style={{ textAlign: 'right' }}>Total S/</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {r.ventas.map(v => (
                                        <tr key={v.sale_id}>
                                          <td>{fmtDate(v.sale_date)}</td>
                                          <td>{v.serie && v.sequence ? `${v.document_type || ''} ${v.serie}-${v.sequence}`.trim() : (v.sale_number || '—')}</td>
                                          <td>{v.client_name}</td>
                                          <td style={{ textAlign: 'right' }}>{fmtN(v.quantity)} {v.unit_measure || ''}</td>
                                          <td style={{ textAlign: 'right' }}>{r.special_condition ? `${fmtN(v.qty_unidad)} UND` : '—'}</td>
                                          <td style={{ textAlign: 'right' }}>{fmtS(v.subtotal)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    {data.tables.porProducto && data.tables.porProducto.length > 0 && (
                      <tfoot>
                        <tr style={{ fontWeight: 800, background: '#f8fafc' }}>
                          <td colSpan={3} style={{ textAlign: 'right' }}>TOTAL VENDIDO</td>
                          <td style={{ textAlign: 'right' }}>{fmtS(data.kpis.total_vendido)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}

            {tab === 'ventas-x-producto' && (
              <div className="rp-card">
                <div className="rp-card-title">
                  <FiTrendingUp /> Ventas por Producto — Detalle — {desde === hasta ? fmtDateOnly(desde) : `${fmtDateOnly(desde)} — ${fmtDateOnly(hasta)}`}
                </div>
                <div className="rp-table-wrap">
                  <table className="rp-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Documento</th>
                        <th>Cliente</th>
                        <th style={{ textAlign: 'right' }}>Cantidad</th>
                        <th style={{ textAlign: 'right' }}>Unidades</th>
                        <th style={{ textAlign: 'right' }}>Total S/</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!data.tables.porProducto || data.tables.porProducto.length === 0) ? (
                        <tr><td colSpan={6} className="rp-empty">Sin ventas en el rango seleccionado</td></tr>
                      ) : data.tables.porProducto.map((r, i) => (
                        <React.Fragment key={rowKey(r) || i}>
                          <tr style={{ background: '#f1f5f9' }}>
                            <td colSpan={3} style={{ fontWeight: 800 }}>{r.product_name}</td>
                            <td style={{ textAlign: 'right', fontWeight: 800 }}>{fmtFormatos(r.formatos)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 800 }}>{r.special_condition ? `${fmtN(r.qty_unidad)} UND` : '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 800 }}>{fmtS(r.total)}</td>
                          </tr>
                          {(!r.ventas || r.ventas.length === 0) ? (
                            <tr><td colSpan={6} className="rp-empty" style={{ paddingLeft: 16 }}>Sin ventas individuales</td></tr>
                          ) : r.ventas.map(v => (
                            <tr key={v.sale_id}>
                              <td>{fmtDate(v.sale_date)}</td>
                              <td>{v.serie && v.sequence ? `${v.document_type || ''} ${v.serie}-${v.sequence}`.trim() : (v.sale_number || '—')}</td>
                              <td>{v.client_name}</td>
                              <td style={{ textAlign: 'right' }}>{fmtN(v.quantity)} {v.unit_measure || ''}</td>
                              <td style={{ textAlign: 'right' }}>{r.special_condition ? `${fmtN(v.qty_unidad)} UND` : '—'}</td>
                              <td style={{ textAlign: 'right' }}>{fmtS(v.subtotal)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                    {data.tables.porProducto && data.tables.porProducto.length > 0 && (
                      <tfoot>
                        <tr style={{ fontWeight: 800, background: '#f8fafc' }}>
                          <td colSpan={5} style={{ textAlign: 'right' }}>TOTAL VENDIDO</td>
                          <td style={{ textAlign: 'right' }}>{fmtS(data.kpis.total_vendido)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}

            {tab === 'cuentas-cobrar' && (
              <>
                <div className="rp-two-col">
                  <Tabla titulo="Antigüedad de Deuda" icono={<FiPieChart />} rows={data.tables.antiguedad} cols={[
                    { k: 'bucket', label: 'Rango' },
                    { k: 'total', label: 'Saldo', align: 'right', fmt: fmtS },
                  ]} />
                  <Tabla titulo="Por Estado" icono={<FiUsers />} rows={data.tables.porEstado} cols={[
                    { k: 'status', label: 'Estado', fmt: badgeEstado },
                    { k: 'n', label: 'N°', align: 'right' },
                    { k: 'saldo', label: 'Saldo', align: 'right', fmt: fmtS },
                  ]} />
                </div>
                <Tabla titulo="Saldos por Cliente" icono={<FiUsers />} rows={data.tables.saldosPorCliente} cols={[
                  { k: 'client_name', label: 'Cliente' },
                  { k: 'n_cuentas', label: 'N° Cuentas', align: 'right' },
                  { k: 'saldo', label: 'Saldo', align: 'right', fmt: fmtS },
                ]} />
                <Tabla titulo="Detalle de Cuentas" icono={<FiUsers />} rows={data.tables.detalle} cols={[
                  { k: 'client_name', label: 'Cliente' },
                  { k: 'sale_number', label: 'N° Venta' },
                  { k: 'due_date', label: 'Vencimiento', fmt: fmtDateOnly },
                  { k: 'amount', label: 'Monto', align: 'right', fmt: fmtS },
                  { k: 'paid_amount', label: 'Cobrado', align: 'right', fmt: fmtS },
                  { k: 'dias_vencido', label: 'Días Venc.', align: 'right', fmt: v => v > 0 ? <span style={{ color: '#dc2626', fontWeight: 700 }}>{v}</span> : '—' },
                  { k: 'status', label: 'Estado', fmt: badgeEstado },
                ]} />
                <Tabla titulo="Cobranzas por Tipo de Ingreso" icono={<FiCreditCard />} rows={data.tables.cobrosPorTipo} cols={[
                  { k: 'payment_method', label: 'Forma de Pago' },
                  { k: 'n', label: 'N° Cobros', align: 'right' },
                  { k: 'total', label: 'Monto Total', align: 'right', fmt: fmtS },
                ]} />
                <Tabla titulo="Cobros del Periodo" icono={<FiTrendingUp />} rows={data.tables.cobrosDetalle} cols={[
                  { k: 'paid_at', label: 'Fecha', fmt: fmtDate },
                  { k: 'client_name', label: 'Cliente' },
                  { k: 'payment_method', label: 'Forma de Pago' },
                  { k: 'amount', label: 'Monto', align: 'right', fmt: fmtS },
                ]} />
              </>
            )}

            {tab === 'cuentas-pagar' && (
              <>
                <div className="rp-two-col">
                  <Tabla titulo="Saldos por Proveedor" icono={<FiCreditCard />} rows={data.tables.saldosPorProveedor} cols={[
                    { k: 'provider_name', label: 'Proveedor' },
                    { k: 'n_cuentas', label: 'N° Cuentas', align: 'right' },
                    { k: 'saldo', label: 'Saldo', align: 'right', fmt: fmtS },
                  ]} />
                  <Tabla titulo="Por Estado" icono={<FiCreditCard />} rows={data.tables.porEstado} cols={[
                    { k: 'status', label: 'Estado', fmt: badgeEstado },
                    { k: 'n', label: 'N°', align: 'right' },
                    { k: 'saldo', label: 'Saldo', align: 'right', fmt: fmtS },
                  ]} />
                </div>
                <Tabla titulo="Detalle de Cuentas" icono={<FiCreditCard />} rows={data.tables.detalle} cols={[
                  { k: 'provider_name', label: 'Proveedor' },
                  { k: 'purchase_number', label: 'N° Compra' },
                  { k: 'due_date', label: 'Vencimiento', fmt: fmtDateOnly },
                  { k: 'amount', label: 'Monto', align: 'right', fmt: fmtS },
                  { k: 'paid_amount', label: 'Pagado', align: 'right', fmt: fmtS },
                  { k: 'dias_vencido', label: 'Días Venc.', align: 'right', fmt: v => v > 0 ? <span style={{ color: '#dc2626', fontWeight: 700 }}>{v}</span> : '—' },
                  { k: 'status', label: 'Estado', fmt: badgeEstado },
                ]} />
                <Tabla titulo="Pagos del Periodo" icono={<FiTrendingUp />} rows={data.tables.pagosDetalle} cols={[
                  { k: 'paid_at', label: 'Fecha', fmt: fmtDate },
                  { k: 'provider_name', label: 'Proveedor' },
                  { k: 'payment_method', label: 'Forma de Pago' },
                  { k: 'amount', label: 'Monto', align: 'right', fmt: fmtS },
                ]} />
              </>
            )}

            {tab === 'cobranzas' && (
              <>
                {data.cliente && (
                  <div className="rp-card" style={{ marginBottom: 0 }}>
                    <div className="rp-card-title"><FiUsers /> {data.cliente.client_name}</div>
                    {data.cliente.document_number && <div style={{ fontSize: 12, color: '#64748b', paddingLeft: 2 }}>{data.cliente.document_number}</div>}
                  </div>
                )}
                {data.kpis.n_inconsistencias > 0 && (
                  <div className="rp-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiAlertTriangle /> Se encontraron {data.kpis.n_inconsistencias} venta(s) donde el monto cobrado no cuadra con la suma real de sus cobros — marcadas abajo con "NO CUADRA".
                  </div>
                )}
                <div className="rp-card">
                  <div className="rp-card-title"><FiFileText /> Ventas y Detalle de su Cobranza</div>
                  {(!data.tables.ventas || data.tables.ventas.length === 0) ? (
                    <div className="rp-empty">Sin ventas a crédito en el rango seleccionado</div>
                  ) : data.tables.ventas.map(v => (
                    <div key={v.id} className="rp-venta-block">
                      <div className="rp-venta-header">
                        <span className="rp-venta-doc">
                          {fmtDate(v.sale_date)} — {v.serie ? `${v.document_type || ''} ${v.serie}-${v.sequence}`.trim() : (v.sale_number || '—')}
                        </span>
                        {badgeEstado(v.status)}
                        {v.descuadre && <span className="rp-venta-descuadre">⚠ NO CUADRA</span>}
                        <span className="rp-venta-monto">{fmtS(v.amount)}</span>
                      </div>
                      {v.cobros.length === 0 ? (
                        <div className="rp-empty" style={{ padding: '6px 4px' }}>Sin cobros registrados</div>
                      ) : (
                        <table className="rp-table rp-subtable">
                          <thead>
                            <tr>
                              <th>N° Cobro</th><th>Fecha Cobro</th><th>Forma de Pago</th><th>Usuario</th>
                              <th style={{ textAlign: 'right' }}>Monto</th><th>Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {v.cobros.map(c => (
                              <tr key={c.id} style={c.anulado ? { color: '#94a3b8', textDecoration: 'line-through' } : {}}>
                                <td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{c.cobranza_id ?? c.id}</td>
                                <td>{fmtDate(c.paid_at)}</td>
                                <td>{c.payment_method}</td>
                                <td>{c.username}</td>
                                <td style={{ textAlign: 'right' }}>{fmtS(c.amount)}</td>
                                <td>{c.anulado ? 'ANULADO' : 'Activo'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {v.restante > 0.009 && (
                        <div className="rp-venta-saldo">Saldo: {fmtS(v.restante)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ); })()}
      </div>
    </div>
  );
}
